import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Pax Avatar — a friendly smiling dolphin facing forward.
 * Keeps the previous "face" personality (two eyes, cheeks, smile) but on a dolphin silhouette.
 * Props:
 *  - state: "idle" | "listening" | "speaking" | "thinking"
 *  - emotion: "attentive" | "warm" | "concerned" | "playful" | "calm"
 *  - size: number (px)
 *  - audioElement: HTMLAudioElement (optional, drives mouth open via WebAudio analyser)
 */

const EMOTION_PALETTES = {
    attentive: {
        top: "#BFD5EA",
        mid: "#84A8CE",
        bottom: "#5B85B4",
        belly: "#EEF4FB",
        glow: "rgba(132,168,206,0.42)",
        water: "rgba(132,168,206,0.14)",
        cheek: "#EDBCB6",
    },
    warm: {
        top: "#C6D9EC",
        mid: "#93B4D3",
        bottom: "#6B93C0",
        belly: "#F1F5FB",
        glow: "rgba(147,180,211,0.42)",
        water: "rgba(147,180,211,0.14)",
        cheek: "#EFBFB4",
    },
    concerned: {
        top: "#C8CFD8",
        mid: "#8B95A1",
        bottom: "#65707D",
        belly: "#E3E7ED",
        glow: "rgba(139,149,161,0.36)",
        water: "rgba(139,149,161,0.12)",
        cheek: "#CBB2AB",
    },
    playful: {
        top: "#C8DEF0",
        mid: "#7EACD3",
        bottom: "#5B85B4",
        belly: "#EFF5FB",
        glow: "rgba(126,172,211,0.5)",
        water: "rgba(126,172,211,0.17)",
        cheek: "#EEB4AC",
    },
    calm: {
        top: "#CDD5C5",
        mid: "#94A390",
        bottom: "#7C8B78",
        belly: "#E7ECDF",
        glow: "rgba(148,163,144,0.4)",
        water: "rgba(148,163,144,0.14)",
        cheek: "#D3B4A8",
    },
};

export default function Avatar({ state = "idle", emotion = "attentive", size = 320, audioElement = null, testId = "avatar" }) {
    const [mouthOpen, setMouthOpen] = useState(0);
    const [blink, setBlink] = useState(false);
    const [bubbles, setBubbles] = useState([]);
    const rafRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);
    const dataRef = useRef(null);

    const palette = EMOTION_PALETTES[emotion] || EMOTION_PALETTES.attentive;

    // Blink loop
    useEffect(() => {
        let t;
        const loop = () => {
            setBlink(true);
            setTimeout(() => setBlink(false), 140);
            t = setTimeout(loop, 2400 + Math.random() * 3500);
        };
        t = setTimeout(loop, 1500);
        return () => clearTimeout(t);
    }, []);

    // Thinking bubbles from blowhole
    useEffect(() => {
        if (state !== "thinking") {
            setBubbles([]);
            return;
        }
        let id = 0;
        const interval = setInterval(() => {
            id += 1;
            const b = { id, x: (Math.random() - 0.5) * 10, r: 2 + Math.random() * 3, dur: 1.8 + Math.random() * 1.2 };
            setBubbles((prev) => [...prev.slice(-4), b]);
            setTimeout(() => setBubbles((prev) => prev.filter((x) => x.id !== b.id)), (b.dur + 0.4) * 1000);
        }, 600);
        return () => clearInterval(interval);
    }, [state]);

    // Audio analyser for speaking lip-sync
    useEffect(() => {
        if (!audioElement) return;
        if (state !== "speaking") {
            setMouthOpen(0);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            return;
        }
        try {
            if (!audioCtxRef.current) {
                audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
            }
            if (!sourceRef.current) {
                sourceRef.current = audioCtxRef.current.createMediaElementSource(audioElement);
                analyserRef.current = audioCtxRef.current.createAnalyser();
                analyserRef.current.fftSize = 256;
                sourceRef.current.connect(analyserRef.current);
                analyserRef.current.connect(audioCtxRef.current.destination);
                dataRef.current = new Uint8Array(analyserRef.current.frequencyBinCount);
            }
            audioCtxRef.current.resume();
            const tick = () => {
                analyserRef.current.getByteFrequencyData(dataRef.current);
                let sum = 0;
                for (let i = 2; i < 24; i++) sum += dataRef.current[i];
                const avg = sum / 22 / 255;
                setMouthOpen(Math.min(1, avg * 2.2));
                rafRef.current = requestAnimationFrame(tick);
            };
            tick();
        } catch (e) {
            console.warn("Avatar audio analyser failed", e);
        }
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [state, audioElement]);

    // Emotion-driven subtle posture
    const headTilt = state === "listening" ? -3 : (emotion === "concerned" ? 1.5 : emotion === "playful" ? -1.5 : 0);
    const swimSpeed = emotion === "playful" ? 2.8 : emotion === "concerned" ? 5.2 : emotion === "calm" ? 5.8 : 4.2;
    const glow = state === "speaking" || state === "listening";

    // Smile shape — Pax always smiles; concerned is just a softer, gentler smile
    const smileCurve = emotion === "concerned" ? 7 : emotion === "playful" ? 18 : 12;
    const browTilt = emotion === "playful" ? 2 : 0;

    // Mouth open while speaking (spring driven by analyser)
    const mouthH = 6 + mouthOpen * 22;
    const mouthW = 36 - mouthOpen * 6;

    const gradId = `pax-d-body-${emotion}`;
    const bellyId = `pax-d-belly-${emotion}`;
    const waterId = `pax-d-water-${emotion}`;

    return (
        <div
            data-testid={testId}
            data-emotion={emotion}
            data-state={state}
            className="relative flex items-center justify-center"
            style={{ width: size, height: size }}
        >
            {/* Soft radial glow when active */}
            <AnimatePresence>
                {glow && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 0.55, scale: 1.05 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8 }}
                        className="absolute inset-0"
                        style={{
                            background: `radial-gradient(circle, ${palette.glow} 0%, rgba(0,0,0,0) 65%)`,
                            filter: "blur(20px)",
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Concentric water ripples */}
            <svg
                viewBox="0 0 240 240"
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ overflow: "visible" }}
            >
                <defs>
                    <radialGradient id={waterId} cx="50%" cy="55%" r="50%">
                        <stop offset="0%" stopColor={palette.water} stopOpacity="0.9" />
                        <stop offset="100%" stopColor={palette.water} stopOpacity="0" />
                    </radialGradient>
                </defs>
                <circle cx="120" cy="125" r="105" fill={`url(#${waterId})`} />
                {[0, 1, 2].map((i) => (
                    <motion.ellipse
                        key={i}
                        cx="120"
                        cy="125"
                        rx="95"
                        ry="24"
                        fill="none"
                        stroke={palette.mid}
                        strokeOpacity={0.18}
                        strokeWidth="1.2"
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: [0.5, 1.05, 1.15], opacity: [0, 0.35, 0] }}
                        transition={{ duration: 4.5, delay: i * 1.5, repeat: Infinity, ease: "easeOut" }}
                        style={{ transformOrigin: "120px 125px" }}
                    />
                ))}
            </svg>

            {/* Dolphin head — gentle float, or a slow inhale/exhale when breathing together */}
            <motion.div
                animate={state === "breathing" ? {
                    scale: [1, 1.06, 1.06, 1],
                    y: [0, -6, -6, 0],
                    rotate: [headTilt, headTilt, headTilt, headTilt],
                } : {
                    scale: 1,
                    y: [0, -5, 0, 4, 0],
                    rotate: [headTilt - 1, headTilt + 1.2, headTilt - 0.5, headTilt + 0.8, headTilt - 1],
                }}
                transition={state === "breathing"
                    ? { duration: 4, times: [0, 0.4, 0.55, 1], repeat: Infinity, ease: "easeInOut" }
                    : { duration: swimSpeed, repeat: Infinity, ease: "easeInOut" }}
                className="relative"
                style={{ width: size * 0.82, height: size * 0.82 }}
            >
                <svg viewBox="0 0 200 200" className="w-full h-full" style={{ overflow: "visible" }}>
                    <defs>
                        <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor={palette.top} />
                            <stop offset="55%" stopColor={palette.mid} />
                            <stop offset="100%" stopColor={palette.bottom} />
                        </linearGradient>
                        <linearGradient id={bellyId} x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor={palette.mid} stopOpacity="0.0" />
                            <stop offset="55%" stopColor={palette.belly} stopOpacity="0.85" />
                            <stop offset="100%" stopColor={palette.belly} stopOpacity="1" />
                        </linearGradient>
                    </defs>

                    {/* Dorsal fin peeking up behind */}
                    <path
                        d={state === "listening"
                            ? "M 116 22 C 126 -2, 150 -2, 160 22 C 146 24, 130 26, 116 22 Z"
                            : "M 116 28 C 124 6, 146 4, 156 26 C 146 28, 130 30, 116 28 Z"}
                        fill={palette.bottom}
                        opacity="0.85"
                        style={{ transition: "opacity 0.6s" }}
                    />

                    {/* Pectoral fins — left & right side flippers */}
                    <motion.g
                        style={{ transformOrigin: "30px 130px" }}
                        animate={{ rotate: [-5, 6, -5] }}
                        transition={{ duration: swimSpeed * 0.7, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <path
                            d="M 36 122 Q 4 138 12 158 Q 30 152 44 138 Z"
                            fill={palette.bottom}
                            opacity="0.92"
                        />
                    </motion.g>
                    <motion.g
                        style={{ transformOrigin: "170px 130px" }}
                        animate={{ rotate: [5, -6, 5] }}
                        transition={{ duration: swimSpeed * 0.7, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <path
                            d="M 164 122 Q 196 138 188 158 Q 170 152 156 138 Z"
                            fill={palette.bottom}
                            opacity="0.92"
                        />
                    </motion.g>

                    {/* Main head — egg shape with a narrowing rostrum at the bottom */}
                    <path
                        d="
                            M 100 22
                            C 56 22, 28 56, 28 96
                            C 28 132, 46 156, 76 168
                            L 84 178
                            Q 100 188 116 178
                            L 124 168
                            C 154 156, 172 132, 172 96
                            C 172 56, 144 22, 100 22
                            Z
                        "
                        fill={`url(#${gradId})`}
                    />

                    {/* Soft belly highlight */}
                    <path
                        d="
                            M 52 122
                            C 80 156, 120 156, 148 122
                            C 140 158, 110 174, 100 178
                            C 90 174, 60 158, 52 122
                            Z
                        "
                        fill={`url(#${bellyId})`}
                        opacity="0.9"
                    />

                    {/* Blowhole on top + thinking bubbles */}
                    <ellipse cx="100" cy="34" rx="3.5" ry="1.6" fill="#3A3532" opacity="0.4" />
                    <AnimatePresence>
                        {bubbles.map((b) => (
                            <motion.circle
                                key={b.id}
                                cx={100 + b.x}
                                cy={34}
                                r={b.r}
                                fill="none"
                                stroke={palette.bottom}
                                strokeOpacity={0.55}
                                strokeWidth="1.2"
                                initial={{ y: 0, opacity: 0 }}
                                animate={{ y: -38, opacity: [0, 0.7, 0] }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: b.dur, ease: "easeOut" }}
                            />
                        ))}
                    </AnimatePresence>

                    {/* Brows (subtle) — relaxed, never furrowed */}
                    {emotion !== "attentive" && (
                        <g stroke="#3A3532" strokeWidth="2" strokeLinecap="round" opacity="0.55" fill="none">
                            <path d={`M 60 ${82 + browTilt} q 10 -5 20 -1`} />
                            <path d={`M 120 ${82 - browTilt} q 10 -1 20 -5`} />
                        </g>
                    )}

                    {/* Eyes — two friendly cartoon eyes */}
                    <g>
                        <motion.ellipse
                            cx="74" cy="96"
                            rx="8"
                            ry={10}
                            fill="#3A3532"
                            initial={{ ry: 10 }}
                            animate={{ ry: blink ? 0.5 : (state === "listening" ? 11 : 10) }}
                            transition={{ duration: 0.12 }}
                        />
                        <motion.ellipse
                            cx="126" cy="96"
                            rx="8"
                            ry={10}
                            fill="#3A3532"
                            initial={{ ry: 10 }}
                            animate={{ ry: blink ? 0.5 : (state === "listening" ? 11 : 10) }}
                            transition={{ duration: 0.12 }}
                        />
                        {/* Eye highlights */}
                        {!blink && (
                            <>
                                <circle cx="77" cy="92" r="2.4" fill="#FFF6EC" opacity="0.95" />
                                <circle cx="129" cy="92" r="2.4" fill="#FFF6EC" opacity="0.95" />
                                <circle cx="71.5" cy="100" r="1.1" fill="#FFF6EC" opacity="0.6" />
                                <circle cx="123.5" cy="100" r="1.1" fill="#FFF6EC" opacity="0.6" />
                            </>
                        )}
                    </g>

                    {/* Cheeks — soft blush on each side */}
                    <ellipse cx="56" cy="128" rx="12" ry="6" fill={palette.cheek} opacity="0.55" />
                    <ellipse cx="144" cy="128" rx="12" ry="6" fill={palette.cheek} opacity="0.55" />

                    {/* Mouth — a clear smile (curve), or open oval when speaking */}
                    {state === "speaking" ? (
                        <motion.ellipse
                            cx="100"
                            cy="146"
                            rx={mouthW / 2}
                            ry={mouthH / 2}
                            fill="#3A3532"
                            initial={{ rx: 18, ry: 3 }}
                            animate={{ rx: mouthW / 2, ry: mouthH / 2 }}
                            transition={{ type: "spring", stiffness: 240, damping: 18 }}
                        />
                    ) : (
                        <path
                            d={state === "thinking"
                                ? "M 82 141 Q 100 148 118 141"
                                : `M 78 140 Q 100 ${140 + smileCurve} 122 140`}
                            stroke="#3A3532"
                            strokeWidth="3.2"
                            strokeLinecap="round"
                            fill="none"
                        />
                    )}

                    {/* Tiny tongue/inner-mouth tint when speaking wide */}
                    {state === "speaking" && mouthOpen > 0.35 && (
                        <ellipse
                            cx="100"
                            cy={150 + mouthOpen * 4}
                            rx={mouthW / 2 - 4}
                            ry={mouthH / 4}
                            fill={palette.cheek}
                            opacity="0.55"
                        />
                    )}
                </svg>
            </motion.div>

            <div
                data-testid="avatar-status"
                className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-xs uppercase tracking-[0.2em] text-inkSoft font-body"
            >
                {state === "listening" && "listening"}
                {state === "speaking" && "speaking"}
                {state === "thinking" && "thinking"}
                {state === "breathing" && "breathing with you… in… and out"}
                {state === "idle" && (
                    emotion === "concerned" ? "holding space" :
                    emotion === "playful" ? "swimming with you" :
                    emotion === "calm" ? "drifting with you" :
                    emotion === "warm" ? "warm with you" :
                    "here with you"
                )}
            </div>
        </div>
    );
}
